import { FC, useCallback } from "react";
import { useController, useFormContext } from "react-hook-form";
import { useParams } from "react-router-dom";
import { Stack, Typography } from "@mui/material";
import { InnerBox } from "@/components/elements/InnerBox";
import { Switch } from "@/components/elements/Switch";
import { text } from "@/theme/colors";
import { TCompanyForm } from "@/features/company/types/companyForm";
import { useGetIndustryList } from "@/api/services/global/industry";

type TCompanyHandledIndustryFormProps = {};

export const CompanyHandledIndustryForm: FC<TCompanyHandledIndustryFormProps> = () => {
  // Edit mode has the route id; pass it so BE scopes hasLearner to this entity. Create has none.
  const { id: companyId } = useParams();
  const { data: industries, isLoading } = useGetIndustryList({ companyId });
  const { control } = useFormContext<TCompanyForm>();
  // useController registers the field so its value reliably reflects defaultValues (edit mode) and updates.
  const {
    field: { value, onChange },
  } = useController({ control, name: "hiddenIndustryIds", defaultValue: [] });
  const hiddenIndustryIds = value ?? [];

  // Toggle ON  -> remove id from hiddenIndustryIds (industry becomes visible)
  // Toggle OFF -> add id to hiddenIndustryIds (industry becomes hidden)
  const handleToggle = useCallback(
    (industryId: number, checked: boolean) => {
      const current = hiddenIndustryIds ?? [];
      const next = checked ? current.filter((id) => id !== industryId) : current.includes(industryId) ? current : [...current, industryId];
      onChange(next);
    },
    [hiddenIndustryIds, onChange],
  );

  return (
    <InnerBox title="取り扱う分野" loading={isLoading}>
      <Typography component="p" variant="cap12" color={text.secondary} mt={1} mb={3}>
        手続きおよび学習進捗を管理・閲覧できる分野を選択ください。
      </Typography>
      <Stack gap={2}>
        {industries.map((industry) => {
          const isEnabled = !hiddenIndustryIds.includes(Number(industry.id));
          // Only block turning OFF: industry currently ON + has a registered learner. An OFF industry can still be turned ON.
          const isDisabled = isEnabled && !!industry.hasLearner;
          return (
            <Stack key={industry.id} gap={0.5}>
              <Switch
                nonControl
                prefixLabel={
                  <Typography component="p" variant="cap12" ml={3} color={text.primary}>
                    {industry.name}
                  </Typography>
                }
                checked={isEnabled}
                disabled={isDisabled}
                onChange={(_, checked) => handleToggle(Number(industry.id), checked)}
              />
              {isDisabled && (
                <Typography component="p" variant="cap12" color={text.secondary} sx={{ ml: "71px" }}>
                  この分野に登録された外国人が存在するため、無効にできません。
                </Typography>
              )}
            </Stack>
          );
        })}
      </Stack>
    </InnerBox>
  );
};
